import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { APIData, ImageCropData, Service } from 'webapp-types';
import { blobUrlToBase64 } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonImageUploadCropComponent from 'webapp/components/common/image-upload/crop';
import CommonImageUploadDropComponent from 'webapp/components/common/image-upload/drop';
import SubmitButtonComponent from 'webapp/components/submit-button';

interface Args {
	imageOriginalUrl?: string;
	imageBlobUrl?: string; // blob url
	imageAspect?: number;
	imageCrop?: ImageCropData | null;
	onFinish: (url: string, originalUrl: string, crop: ImageCropData | null) => void;
	onClose: () => void;
	onImageCrop: (imageCrop: ImageCropData | null) => void;
	onCropActiveChange: (isCropActive: boolean) => void;
}

export default class CommonImageUploadComponent extends Component<Args> {
	@service('navigation') declare navigationService: Service.Navigation;
	@service('request') declare requestService: Service.Request;

	@tracked imageBlobUrl = ''; // bloburl, either passed from outside dropzone or uploaded here
	@tracked imageContent = ''; // base64 encoded
	@tracked imageCrop: ImageCropData | null = null;
	@tracked submitInProgress = false;

	constructor(owner: Owner, args: Args) {
		super(owner, args);
		this.imageBlobUrl = this.args.imageBlobUrl || '';

		const imageCrop = this.args.imageCrop;
		this.imageCrop = imageCrop && Object.keys(imageCrop).length ? imageCrop : null;
	}

	get imageForCropper() {
		// cropper recongizes both types of urls
		const originalUrl = this.args.imageOriginalUrl;
		return this.imageBlobUrl || (originalUrl ? `${originalUrl}?_` : '');
	}

	get isSaveDisabled() {
		const imageCrop = this.imageCrop;
		return this.submitInProgress || !imageCrop || imageCrop.width < 1 || imageCrop.height < 1;
	}

	get imageAspect() {
		return this.args.imageAspect;
	}

	@action
	onImageAdd(blobUrl: string) {
		this.imageBlobUrl = blobUrl;
	}

	@action
	onImageCrop(imageBase64Content: string, crop?: ImageCropData) {
		const newCrop = crop || null;
		this.imageContent = imageBase64Content;
		this.imageCrop = newCrop;
		this.args.onImageCrop(newCrop);
	}

	@action
	save() {
		this.submitInProgress = true;

		let imageOriginalPromise = Promise.resolve('');
		if (this.imageBlobUrl) {
			imageOriginalPromise = blobUrlToBase64(this.imageBlobUrl).then((base64Content) => {
				if (base64Content) {
					return this.uploadFile(base64Content);
				} else {
					return '';
				}
			});
		}

		const imagePromise = this.uploadFile(this.imageContent);

		Promise.all([imageOriginalPromise, imagePromise])
			.then(async () => {
				const url = await imagePromise,
					originalUrl =
						(await imageOriginalPromise) ||
						(url ? this.args.imageOriginalUrl || '' : '');
				this.args.onFinish(url, originalUrl, this.imageCrop);
			})
			.finally(() => {
				this.submitInProgress = false;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	@action
	delete() {
		this.args.onFinish('', '', null);
	}

	@action
	close() {
		this.args.onClose();
	}

	private uploadFile(filedataBase64: string): Promise<string> {
		return new Promise((resolve, reject) => {
			const split = filedataBase64.split(';base64,'),
				filedata = split[1],
				mimetype = split[0]!.replace('data:', ''),
				ext = mimetype.split('/')[1],
				filename = `image_${Math.round(Math.random() * 1000)}_${String(Math.floor(Date.now() / 1000))}.${ext}`;
			this.requestService
				.api<APIData.Image.UploadResponse>('POST', '/image/upload', {
					filename,
					filedata,
					mimetype,
				})
				.then((response) => {
					if (response.url) {
						resolve(response.url);
					} else {
						throw {
							code: 422,
							errors: [
								{
									code: 422,
									name: 'general',
									message: response.error?.message || 'Upload error',
								},
							],
						};
					}
				})
				.catch(reject);
		});
	}
	<template>
		{{#if this.imageForCropper}}

			<CommonImageUploadCropComponent
				@image={{this.imageForCropper}}
				@crop={{this.imageCrop}}
				@imageAspect={{this.imageAspect}}
				@onImageCrop={{this.onImageCrop}}
			/>

			<div
				class="controls2"
				{{didInsert (fn @onCropActiveChange true)}}
				{{willDestroy (fn @onCropActiveChange false)}}
			>
				<div class="controls2__left">
					<SubmitButtonComponent
						@text="Delete"
						@icon="Trash"
						@theme="red-outlined"
						@onTrigger={{this.delete}}
						@isDisabled={{this.submitInProgress}}
					/>
				</div>
				<div class="controls2__right">
					<SubmitButtonComponent
						@text="Cancel"
						@theme="grey-empty"
						@noDelay={{true}}
						@onTrigger={{this.close}}
					/>
					<SubmitButtonComponent
						@text="Crop & save"
						@onTrigger={{this.save}}
						@submitInProgress={{this.submitInProgress}}
						@isDisabled={{this.isSaveDisabled}}
					/>
				</div>
			</div>

		{{else}}
			<CommonImageUploadDropComponent
				@onImageAdd={{this.onImageAdd}}
				@theme={{this.navigationService.theme.id}}
			/>
		{{/if}}
	</template>
}
